// Static filter configuration mapping display values to the chip UI, per the
// find-contractors filter PRD. No longer a like-for-like copy of
// (main_app)/lead-gen-profiles/_component/filterConfig.js — these categories
// were redesigned for this page and have mostly diverged from that page's
// (both still read/write the same useContractorFiltersStore, see store.js,
// but only the `languages` and `experience` keys exist in both configs, and
// even those no longer share the same option values).
//
// This page talks to /v2/api/lead-gen/profiles (../../../_api/contractors.js).
// Per the PRD, `propertyType`, `serviceType`, `materialsProvided`,
// `serviceWarranty` and `languages` are sent straight through to that
// endpoint's `filters` object (chip option strings = the API's enum values)
// — see buildLeadGenProfileFilters below. `experience` stays UI-only: its
// chips are ranges ("0-2 yrs", "2-5 yrs", …) but the endpoint's
// `workExperienceV2` filter is an *exact* match on a single year value, so
// there's no value we could send that means "between 2 and 5" without
// silently dropping legitimate matches.
export const FILTER_CONFIG = {
  propertyType: {
    options: ["Residential", "Commercial"],
    title: "Property type",
  },
  serviceType: {
    options: [
      "False Ceiling & Partitions",
      "Electrical & Lighting",
      "Painting & Wall Finishes",
      "Plumbing & Sanitary",
      "Flooring & Tiling",
      "Waterproofing",
      "Civil & Structural Work",
    ],
    title: "Service type needed",
  },
  materialsProvided: {
    options: ["Material & Labour", "Labour only"],
    title: "Work type",
  },
  serviceWarranty: {
    options: ["1 year", "2 years", "3 years", "5 years", "7 years", "10 years"],
    title: "Service warranty",
  },
  experience: {
    options: ["0-2 yrs", "2-5 yrs", "5-10 yrs", "10+ yrs"],
    title: "Experience",
  },
  languages: {
    options: [
      "Hindi",
      "English",
      "Bengali",
      "Telugu",
      "Marathi",
      "Tamil",
      "Gujarati",
      "Kannada",
      "Malayalam",
      "Punjabi",
      "Odia",
      "Assamese",
      "Urdu",
    ],
    title: "Languages spoken",
  },
};

// Derived from FILTER_CONFIG (not hand-listed) so this always matches its
// current categories instead of drifting out of sync with it. Shared by the
// mobile FilterSheet and the desktop FiltersSidebar — both stage edits in a
// local "pending" selection (keyed by every known category, defaulting to
// []) before writing to the store on Apply.
export const EMPTY_SELECTED = Object.fromEntries(
  Object.keys(FILTER_CONFIG).map((category) => [category, []])
);

/**
 * Map a filter category name to the GTM event value field name used when
 * reporting a chip toggle. Shared by the mobile FilterSheet and the desktop
 * FiltersSidebar so both surfaces report identical event_action values.
 */
export const getCategoryValueField = (category) => {
  const categoryMap = {
    propertyType: "property_type_value",
    serviceType: "service_type_value",
    materialsProvided: "materials_provided_value",
    serviceWarranty: "service_warranty_value",
    experience: "experience_value",
    languages: "languages_value",
  };
  return categoryMap[category] || `${category}_value`;
};

/**
 * Transform the selected filter chips into /v2/api/lead-gen/profiles'
 * `filters` object. `languages`, `propertyType` and `serviceType` are
 * ORed arrays server-side, so the selected chips pass straight through.
 * `materialsProvided` and `serviceWarranty` are single-value filters
 * server-side even though the chip UI allows multi-select — see each
 * branch below for how a multi-select collapses to one value.
 *
 * `experience` is intentionally absent: see the header comment above.
 */
export const buildLeadGenProfileFilters = (selectedFilters = {}) => {
  const filters = {};

  if (selectedFilters.languages?.length) {
    filters.languages = selectedFilters.languages;
  }

  if (selectedFilters.propertyType?.length) {
    filters.propertyType = selectedFilters.propertyType;
  }

  if (selectedFilters.serviceType?.length) {
    filters.serviceType = selectedFilters.serviceType;
  }

  // `material_provided` is a single "yes"/"no" value server-side — picking
  // several chips can only mean "use whichever was picked first".
  if (selectedFilters.materialsProvided?.length) {
    filters.materialsProvided = selectedFilters.materialsProvided[0] === "Material & Labour"
      ? "yes"
      : "no";
  }

  // `service_warranty >= value` server-side. The filter is monotonic (>=5yrs
  // implies >=2yrs), so selecting several chips — an OR over thresholds —
  // collapses to the lowest one: the union of {>=2} and {>=5} is just {>=2}.
  if (selectedFilters.serviceWarranty?.length) {
    const years = selectedFilters.serviceWarranty
      .map((option) => parseInt(option, 10))
      .filter((n) => !Number.isNaN(n));
    if (years.length) filters.serviceWarranty = Math.min(...years);
  }

  return filters;
};
